import { OfferGroupDomain, Offer } from '..';
import { IOfferVariantViewModel } from '../models/offer-group-domain.model';

export type OfferDisplayRow =
  | { kind: 'skeleton'; parentDomainId: string; variantId: string }
  | { kind: 'base'; offer: OfferGroupDomain<Offer> }
  | { kind: 'variant'; offer: OfferGroupDomain<Offer> };

export function isExpandableOffer(offer: OfferGroupDomain<Offer>): boolean {
  const variants = offer?.Variants ?? [];
  return variants.length > 1 && !!offer.EventOfferType && offer.EventOfferType !== 'OFFERGROUP';
}

export function getNonBaseVariants(offer: OfferGroupDomain<Offer>): IOfferVariantViewModel[] {
  const baseId = offer.Detail?.Id;
  return (offer.Variants ?? []).filter((v) => v.Id && v.Id !== baseId);
}

/**
 * Builds flattened rows for offer cards/table: compressed = one row per search result;
 * expanded = base row + skeleton or hydrated variant rows (cache on each offer via _expandedVariant* keys).
 */
export function buildOfferDisplayRows(
  data: OfferGroupDomain<Offer>[],
  variantViewMode: 'compressed' | 'expanded',
): OfferDisplayRow[] {
  if (variantViewMode === 'compressed') {
    return data.map((offer) => ({ kind: 'base', offer }));
  }

  const rows: OfferDisplayRow[] = [];
  for (const offer of data) {
    rows.push({ kind: 'base', offer });
    if (!isExpandableOffer(offer)) {
      continue;
    }
    const nonBase = getNonBaseVariants(offer);
    if (nonBase.length === 0) {
      continue;
    }
    const loading = !!offer['_expandedVariantsLoading'];
    const cached = offer['_expandedVariantCards'] as OfferGroupDomain<Offer>[] | undefined;
    if (loading && (!cached || cached.length === 0)) {
      for (const v of nonBase) {
        if (v.Id) {
          rows.push({ kind: 'skeleton', parentDomainId: offer.DomainId ?? '', variantId: v.Id });
        }
      }
    } else if (cached?.length) {
      for (const card of cached) {
        rows.push({ kind: 'variant', offer: card });
      }
    }
  }
  return rows;
}

export function trackOfferDisplayRow(index: number, row: OfferDisplayRow): string {
  if (row.kind === 'skeleton') {
    return `sk-${row.parentDomainId}-${row.variantId}`;
  }
  if (row.kind === 'variant') {
    const vid = row.offer['_variantId'] ?? row.offer.Detail?.Id ?? index;
    return `v-${vid}`;
  }
  return `b-${row.offer.DomainId ?? index}`;
}

/** Unique DOM id for comments anchor — variant rows use Detail.Id to avoid colliding with base DomainId. */
export function commentsAnchorIdForOffer(offer: OfferGroupDomain<Offer>): string {
  if (offer['_isVariantCard'] && offer.Detail?.Id) {
    return String(offer.Detail.Id);
  }
  return String(offer.DomainId ?? '');
}
